import { useState, useEffect, useRef  } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as amplitude from "@amplitude/analytics-browser";
import type { TCurrencies, TMethods } from '@app/dashboard/types/zustand/integrations';
import type { ICreateBtnLinks, TNavbarSpecialActions } from '@app/dashboard/types/innerLayout/nav-links';
import { environment } from '@app/dashboard/environments/environment';
import { NAVBAR_SPECIAL_ACTIONS } from '@app/dashboard/constants/navbar-special-actions';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { COOKIES_KEYS } from '@app/dashboard/constants/cookies-keys';
import { SEARCH_PARAMS_KEYS } from '@app/dashboard/constants/search-params-keys';
import { ACCOUNT_EVENTS } from '@app/dashboard/constants/tracking_events/account';
import { SHARED_EVENTS, SHARED_EVENTS_PARAMS, SHARED_EVENTS_VALUES } from '@app/dashboard/constants/tracking_events/shared';
import { useIntegrationsStore } from '@app/dashboard/store/integrations';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { useFiltersStore } from '@app/dashboard/store/home/filters';
import { useNavigationStore } from '@app/dashboard/store/navigation';
import { useGlobalStore } from '@app/dashboard/store/global';
import { useUserInfoStore } from '@app/dashboard/store/userInfo';
import { useLangStore } from '@app/dashboard/store/lang';
import { useServicesStore } from '@app/dashboard/store/services';
import { getIntegrations } from '@app/dashboard/services/inner_layout/integrations';
import { mapIntegrationsToPaymentMethods } from '@app/dashboard/utils/mapIntegrationsToPaymentMethods';
import { createBtnLinks, navLinks, pagesInternalRoutes } from '@app/dashboard/services/inner_layout/nav-links';
import { fetchReachPortalSsoUrl } from '@app/dashboard/services/inner_layout/reach/reach_portal';
import { saveTokenWithExpiry, validateSession } from '@app/dashboard/tools/axios/axiosUtils';
import { getFromLocalStorage, handleAuthLogout } from '@app/dashboard/utils/manageLocalStorage';
import { getLang, getMerchantProfileData, getMerchantToken, getProfileType, getChatbotCountry } from '@app/dashboard/utils/localStorageUtils';
import { userInfo as userData } from "@app/dashboard/utils/auth/userInfo";
import { getOldDashboardUrl, UNIFIED_DOCS_URL } from '@app/dashboard/utils/serverUrls';
import { setCrossSubdomainsCookie } from '@app/dashboard/utils/manageCookies';
import { notify } from '@app/dashboard/utils/notifier';
import { getDashboardsCorrespondingRoute } from '@app/dashboard/utils/crossDashboardsRoutes';
import { getCurrentPath } from '@app/dashboard/utils/getCurrentPath';
import { checkActiveServices } from '@app/dashboard/services/inner_layout/services';


const INTEGRATIONS_NUMBER = 500;

// const freshworksWidgetInitializer = () => {

//   const freshworksSourceScript = document.createElement('script');
//   freshworksSourceScript.type = 'text/javascript';
//   freshworksSourceScript.src = 'https://widget.freshworks.com/widgets/48000000323.js';
//   freshworksSourceScript.async = true;
//   freshworksSourceScript.defer = true;
//   document.head.appendChild(freshworksSourceScript);

//   const freshworksSettingsScript = document.createElement('script');
//   freshworksSettingsScript.textContent = `
//     window.fwSettings = { 'widget_id': 48000000323 }; !function () { if ("function" != typeof window.FreshworksWidget) { var n = function () { n.q.push(arguments) }; n.q = [], window.FreshworksWidget = n } }()
// `;
//   document.head.appendChild(freshworksSettingsScript);

//   return { freshworksSourceScript, freshworksSettingsScript };
// };

export const freshchatWidgetInitializer = () => {
  const profileData = getMerchantProfileData();

  const freshchatScript = document.createElement('script');

  const userInfo = {
    externalId: profileData.id,
    firstName: profileData.first_name,
    lastName: profileData.last_name,
    email: profileData.email,
    phone: profileData.phone,
  };

  const initConfig = {
    config: {
      headerProperty: {
        hideChatButton: true
      }
    },
    token: "4e93793d-b569-4de2-9d61-9ac6786df61a",
    host: "https://paymob.freshchat.com",
    tags: environment.FRESH_CHAT_WIDGET_TAGS,
    widgetUuid: environment.FRESH_CHAT_WIDGET_UUID,
    ...userInfo
  };

  freshchatScript.textContent = `

  function initFreshChat() {
    window?.fcWidget?.init(${JSON.stringify(initConfig)});

  if (window?.fcWidget?.user) {
    window.fcWidget.setExternalId(${JSON.stringify(profileData.id)});
    window.fcWidget.user.setProperties(${JSON.stringify(userInfo)});   
  }
  }
  function initialize(i, t) {
    var e; i.getElementById(t) ?
      initFreshChat() : ((e = i.createElement("script")).id = t, e.async = !0,
        e.src = "https://paymob.freshchat.com/js/widget.js", e.onload = initFreshChat, i.head.appendChild(e))
  }
  function initiateCall() { initialize(document, "Freshchat-js-sdk") };
  initiateCall();
  `;

  document.head.appendChild(freshchatScript);

  return freshchatScript;
};

export const chatbotWidgetInitializer = () => {
  const token = getMerchantToken();
  const region = getChatbotCountry();

  if (!token) return;

  const sdkScript = document.createElement('script');
  sdkScript.src = 'https://cdn.jsdelivr.net/npm/paymob-chatbot@latest/main.js';
  sdkScript.onload = () => {
    const initScript = document.createElement('script');
    initScript.textContent = `
      window.__paymobChatbotInstance = new window.PaymobChatbot({
        auth: {
          token: ${JSON.stringify(token)},
          platform: 'merchant-dashboard',
          region: ${JSON.stringify(region)},
          user_type: 'merchant'
        },
        showToggle: false
      });
    `;
    document.head.appendChild(initScript);
  };

  document.head.appendChild(sdkScript);

  return sdkScript;
};

export function useInnerLayoutLogic() {
  const { t } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    setHasLiveIntegration,
    setCurrencies,
    setMethods,
    setPaymentMethodIntegrations,
    setIsError: setIsIntegrationsError,
    setIsLoading: setIsIntegrationsLoading
  } = useIntegrationsStore();
  const { isLive, setIsLive } = useTestLiveModeStore();
  const { setFilters } = useFiltersStore();
  const { setActiveLink, setSpecialAction } = useNavigationStore();
  const { isAuthenticated, setIsAuthenticated } = useGlobalStore();
  const { setUserInfo } = useUserInfoStore();
  const { setLang } = useLangStore();
  const {
    setIsDigestEnabled: setIsDigestEnabledInStore,
    setIsMerchantStatementEnabled: setIsMerchantStatementEnabledInStore,
    setIsLoading: setIsServicesLoading,
  } = useServicesStore();

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isWidgetsLoaded, setIsWidgetsLoaded] = useState<Record<Extract<TNavbarSpecialActions, "live-chat" | "support">, boolean>>({
    "live-chat": false,
    "support": false,
  });

  const {
    isLoading,
    isError,
    isFetching,
    data: integrationsData,
    isFetched
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_INTEGRATIONS],
    queryFn: () => getIntegrations(INTEGRATIONS_NUMBER),
    enabled: isAuthenticated === true
  });

  const {
    isLoading: isServicesLoading,
    isFetching: isServicesFetching,
    data: servicesData,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_SERVICES],
    queryFn: () => checkActiveServices(),
    enabled: isAuthenticated === true
  });

  useEffect(() => {
    setIsIntegrationsError(isError);
  }, [isError]);

  useEffect(() => {
    setIsIntegrationsLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    setIsServicesLoading(isServicesLoading || isServicesFetching);
  }, [isServicesLoading, isServicesFetching]);

  useEffect(() => {
    let hasLiveIntegrations = false;

    if (integrationsData && !isFetching) {

      let currencies: TCurrencies = {
        test: [],
        live: []
      };

      let methods: TMethods = {
        test: [],
        live: []
      };

      const integrations = integrationsData?.data?.data?.results;

      integrations?.forEach((
        { is_live, currency, gateway_type }
          :
          { is_live: boolean, currency: string, gateway_type: string }) => {
        const mode = is_live ? "live" : "test";

        if (!hasLiveIntegrations && is_live) {
          hasLiveIntegrations = true
        };

        (!currencies?.[mode].includes(currency)) && currencies[mode].push(currency);
        (!methods?.[mode].includes(gateway_type)) && methods[mode].push(gateway_type);
      });

      setHasLiveIntegration(hasLiveIntegrations);
      setCurrencies(currencies);
      setMethods(methods);

      const mappedData = mapIntegrationsToPaymentMethods(integrationsData);
      setPaymentMethodIntegrations(mappedData);
    }
  }, [integrationsData, isFetching, isFetched, setHasLiveIntegration, setCurrencies, setMethods, setPaymentMethodIntegrations]);

  useEffect(() => {
    const digestData = getFromLocalStorage(LOCAL_STORAGE_KEYS.DIGEST);
    const merchantStatementsData = getFromLocalStorage(LOCAL_STORAGE_KEYS.MERCHANT_STATEMENTS);

    setIsDigestEnabledInStore(digestData?.enabled);
    setIsMerchantStatementEnabledInStore(merchantStatementsData?.enabled);

  }, [servicesData, isServicesFetching]);

  useEffect(() => {
    typeof isLive === 'boolean' && setFilters({
      is_live: isLive
    });
  }, [isLive]);

  useEffect(() => {
    const path = location.pathname;
    let activeLinks = getActiveLinks(path);

    if (!activeLinks) {
      // If not part of the side menu, search in internal routes
      pagesInternalRoutes(t).forEach((route) => {

        if (matchPath({ path: route.path, end: true }, path)) {
          activeLinks = { nav: route.targetLink.nav, sub: route.targetLink.sub || '' };
        }
      })
    }

    activeLinks && setActiveLink(activeLinks);

  }, [location, t]);
   
  const isOpeningReachPortalRef = useRef(false);

  const { mutateAsync: mutateReachPortalSso, isPending: isReachPortalLoading } = useMutation({
    mutationKey: [QUERY_KEYS.REACH_PORTAL_SSO],
    mutationFn: () => fetchReachPortalSsoUrl(),
    onSuccess: (res) => {
      const redirectUrl = res?.data?.data?.data?.redirect_url;
      if (redirectUrl) {
        window.open(redirectUrl);
      } else {
        notify.error(t('innerLayout.reach.reachPortal.error'));
      }
    },
    onError: () => {
      notify.error(t('innerLayout.reach.reachPortal.error'));
    },
    onSettled: () => {
      isOpeningReachPortalRef.current = false;
    },
  });

  const handleOpenReachPortal = async () => {
    if (isOpeningReachPortalRef.current || isReachPortalLoading) return;
    isOpeningReachPortalRef.current = true;
    notify.primary(t('loading'));
    mutateReachPortalSso();
  };


  const handleNavbarSpecialActions = (type: TNavbarSpecialActions) => {
    const WINDOW: any = window;

    // type === NAVBAR_SPECIAL_ACTIONS.SUPPORT && (WINDOW?.FreshworksWidget?.('open'), WINDOW?.fcWidget?.close());
    type === NAVBAR_SPECIAL_ACTIONS.LIVE_CHAT && (WINDOW?.fcWidget?.open(), WINDOW?.__paymobChatbotInstance?.close());
    type === NAVBAR_SPECIAL_ACTIONS.CHATBOT && (WINDOW?.__paymobChatbotInstance?.open(), WINDOW?.fcWidget?.close());
    type === NAVBAR_SPECIAL_ACTIONS.LANGUAGE && setLang();
    type === NAVBAR_SPECIAL_ACTIONS.SWITCH_DASHBOARD && handleSwitchDashboard();
    type === NAVBAR_SPECIAL_ACTIONS.FEEDBACK && setIsFeedbackModalOpen(true);
    type === NAVBAR_SPECIAL_ACTIONS.REACH_PORTAL && handleOpenReachPortal();
  };

  const onTestToggleChange = (checked: boolean) => {
    typeof isLive === 'boolean' && setIsLive(!checked);
  };

  const editProfileHandler = () => {
    // console.log("Edit profile");
  };

  const logoutHandler = async () => {
    // Invalidate all queries to clear cache between accounts
    queryClient.clear();
    handleAuthLogout();
    setIsAuthenticated(false);
    await amplitude.track(ACCOUNT_EVENTS.SIGNOUT_CLICKED, {
      [SHARED_EVENTS_PARAMS.FROM_PAGE]: getCurrentPath()
    }).promise;
  
    amplitude.reset();
  };

  const getActiveLinks = (route: string) => {
    const navLinksList = [...navLinks(t), ...createBtnLinks(t)];

    // Find if the route exists in navlinks
    const navLink = navLinksList.find(item => matchPath({ path: item.route || '', end: true }, route));

    if (navLink) return { nav: navLink.label || "", sub: "" };


    // Find if the route exists in subLinks
    const parentLink = navLinksList.find(item => 'subLinks' in item && item.subLinks?.some(sub => matchPath({ path: sub.route || '', end: true }, route)));

    if (parentLink && 'subLinks' in parentLink) {
      const subLink = parentLink.subLinks?.find(sub => matchPath({ path: sub.route || '', end: true }, route));
      return { nav: parentLink.label || "", sub: subLink?.label || "" };
    }
  };

  const handleSwitchDashboard = async (freshAccessToken?: string, openInNewTab: boolean = false) => {
    const token = freshAccessToken || await validateSession();

    const lang = getLang();
    const oldDashboardUrl = getOldDashboardUrl();
    const targetPathname = getDashboardsCorrespondingRoute(window.location.pathname, 'old');
    const hostname = window.location.hostname;
    const domain = hostname === 'localhost' ?
      'localhost'
      :
      '.paymob.com';

    await setCrossSubdomainsCookie({
      name: COOKIES_KEYS.SWITCH_DASHBOARDS_TOKEN,
      value: token,
      domain: domain
    });

    window.open(
      `${oldDashboardUrl}/${lang}/${targetPathname}?${SEARCH_PARAMS_KEYS.SWITCH_DASHBOARDS_INTERFACE}=true`,
      openInNewTab ? "_blank" : "_self"
    );

  };

  const onCreateMenuOpen = (open: boolean) => {
    open && amplitude.track(SHARED_EVENTS.CREATE_MENU_OPENED, {
      [SHARED_EVENTS_PARAMS.CURRENT_PAGE]: getCurrentPath(),
      [SHARED_EVENTS_PARAMS.ENTRY_POINT]: SHARED_EVENTS_VALUES.TOP_BAR
    });
  };

  const onCreateOptionSelected = (item: ICreateBtnLinks) => {
    amplitude.track(SHARED_EVENTS.CREATE_OPTION_SELECTED, {
      [SHARED_EVENTS_PARAMS.CREATE_OPTION]: item.trackingLabel || '',
      [SHARED_EVENTS_PARAMS.CURRENT_PAGE]: getCurrentPath()
    });

    navigate(item.route);
    setActiveLink({ nav: item.label || '' });
  };

  useEffect(() => {
    // const WINDOW: any = window;
    // freshworksWidgetInitializer();
    // freshchatWidgetInitializer();

    // Hide freshworks widget trigger btn
    // WINDOW?.FreshworksWidget?.('hide', 'launcher');

    // Set support btn actions (open widgets)
    setSpecialAction?.(handleNavbarSpecialActions);

    // Set user info
    setUserInfo({ ...userData() });

    // return () => {
    //   try {
    //     // WINDOW?.fcWidget?.destroy?.();
    //     // WINDOW?.FreshworksWidget?.('destroy');
    //   } catch (error) {
    //     console.log('error :', error);
    //     // Ignore destroy errors when widget is not mounted
    //   }
    // };
  }, []);

  useEffect(() => {
    // this useEffect used to enable the support dropdown items once they're loaded

    const observeWidget = () => {

      // const supportWidgetIframe: any = document.querySelectorAll('iframe#freshworks-frame')[0];
      // const supportIframeDocument = supportWidgetIframe?.contentDocument || supportWidgetIframe?.contentWindow?.document;

      let liveChatWidgetIframe: any, liveChatIframeDocument: any;
      try {
        liveChatWidgetIframe = document.querySelectorAll('iframe#fc_widget')[0];
        liveChatIframeDocument = liveChatWidgetIframe?.contentDocument || liveChatWidgetIframe?.contentWindow?.document;
      } catch (error) { console.log('Error accessing live chat iframe document', error) };

      // if (supportIframeDocument && !isWidgetsLoaded["support"]) {
      //   setIsWidgetsLoaded(prevState => ({ ...prevState, "support": true }));
      //   domObserver.disconnect();
      // }

      if (liveChatIframeDocument && !isWidgetsLoaded["live-chat"]) {
        setIsWidgetsLoaded(prevState => ({ ...prevState, "live-chat": true }));
        domObserver.disconnect();
      }
    };

    const domObserver = new MutationObserver(observeWidget);

    (/* !isWidgetsLoaded["support"] || */ !isWidgetsLoaded["live-chat"]) &&
      (domObserver.observe(document.body, { childList: true, subtree: true }));

    return () => domObserver.disconnect();

  }, [isWidgetsLoaded]);

  return {
    onTestToggleChange,
    editProfileHandler,
    logoutHandler,
    handleSwitchDashboard,
    isWidgetsLoaded,
    isReachPortalLoading,
    feedbackModal: {
      setIsOpen: setIsFeedbackModalOpen,
      isOpen: isFeedbackModalOpen,
    },
    onCreateMenuOpen,
    onCreateOptionSelected
  };
}